'use client'

import { useCallback, useMemo } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import { CtaFooter } from '@/modules/academy/components/CtaFooter'

import { VideosGrid } from './components/VideosGrid/VideosGrid'
import { VideosHeader } from './components/VideosHeader/VideosHeader'
import { VideosTopicsFilter } from './components/VideosTopicsFilter/VideosTopicsFilter'
import { filterVideosByTopics } from './utils/filterVideosByTopics'
import { buildTopicsQuery, parseTopicsFromSearchParams } from './utils/parseTopicsQuery'
import { VIDEOS_TEST_IDS } from './videos.const'
import type { AcademyVideo, AcademyVideoTopic } from './videos.interface'

import styles from './VideosPage.module.scss'

type VideosPageProps = {
  topics: AcademyVideoTopic[]
  videos: AcademyVideo[]
}

export function VideosPage({ topics, videos }: VideosPageProps) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const selectedTopicIds = useMemo(() => parseTopicsFromSearchParams(searchParams), [searchParams])

  const filteredVideos = useMemo(() => filterVideosByTopics(videos, selectedTopicIds), [videos, selectedTopicIds])

  const updateTopicsInUrl = useCallback(
    (nextTopicIds: string[]) => {
      const query = buildTopicsQuery(nextTopicIds)
      router.replace(`${pathname}${query}`, { scroll: false })
    },
    [pathname, router]
  )

  const handleTopicToggle = useCallback(
    (topicId: string, checked: boolean) => {
      const next = new Set(selectedTopicIds)
      if (checked) {
        next.add(topicId)
      } else {
        next.delete(topicId)
      }
      updateTopicsInUrl(Array.from(next))
    },
    [selectedTopicIds, updateTopicsInUrl]
  )

  const handleReset = useCallback(() => updateTopicsInUrl([]), [updateTopicsInUrl])

  const handleTryGraftcode = () => {
    const portalUrl = process.env.NEXT_PUBLIC_WEB_PORTAL_URL || 'https://portal.graftcode.com'
    window.open(portalUrl, '_blank')
  }

  return (
    <div className={styles.page} data-testid={VIDEOS_TEST_IDS.page}>
      <div className={styles.body}>
        <div className={styles.mainColumn}>
          <VideosHeader />
          <VideosGrid videos={filteredVideos} />
        </div>
        <VideosTopicsFilter
          topics={topics}
          selectedTopicIds={selectedTopicIds}
          onTopicToggle={handleTopicToggle}
          onReset={handleReset}
        />
      </div>
      <CtaFooter onTryGraftcode={handleTryGraftcode} />
    </div>
  )
}
